import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, MapPin } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { KpiCard } from "@/components/kpi-card";
import { StatusBadge } from "@/components/status-badge";
import { NewShootDialog } from "@/components/forms/new-shoot-dialog";
import {
  shoots, inquiries, galleries, upcomingPayments, kpis, fmtUsd, studio,
} from "@/mocks/data";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Dashboard — Studio OS" },
      { name: "description", content: "Today's shoots, inquiries, invoices, and recent galleries." },
    ],
  }),
  component: Dashboard,
});

function Dashboard() {
  const today = shoots.filter((s) => s.date === "Today");
  const recentInquiries = inquiries.slice(0, 4);
  const recentGalleries = galleries.slice(0, 4);
  const [today_date, setTodayDate] = useState("");
  useEffect(() => {
    setTodayDate(
      new Date().toLocaleDateString("en-US", {
        weekday: "long", month: "long", day: "numeric",
      }),
    );
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader
        title={`Welcome back, ${studio.ownerName.split(" ")[0]}.`}
        description={`${today_date ? `${today_date} — ` : ""}${today.length} active ${today.length === 1 ? "shoot" : "shoots"} scheduled for today.`}
        actions={<NewShootDialog />}
      />

      <div className="mb-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard
          label="Revenue MTD"
          value={fmtUsd(kpis.revenueMtd)}
          hint={
            <div className="flex items-center gap-1.5">
              <span className="rounded bg-success-soft px-1.5 py-0.5 text-[10px] font-medium text-success">
                +{kpis.revenueChangePct}%
              </span>
              <span>vs last month</span>
            </div>
          }
        />
        <KpiCard label="Booked Shoots" value={String(kpis.bookedShoots).padStart(2, "0")} hint={`${kpis.pendingShoots} pending confirmation`} />
        <KpiCard label="Pending Invoices" value={fmtUsd(kpis.pendingInvoices)} hint={`Across ${kpis.pendingClients} clients`} />
        <KpiCard accent label="Awaiting Proofs" value={String(kpis.awaitingProofs).padStart(2, "0")} hint={`Avg turn-around ${kpis.avgTurnaround} days`} />
      </div>

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-3">
        <div className="space-y-8 xl:col-span-2">
          {/* Today's schedule */}
          <section>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Today's Schedule
              </h2>
              <Link to="/bookings" className="text-xs font-medium text-brand hover:underline">
                View calendar
              </Link>
            </div>
            <div className="space-y-3">
              {today.map((s, i) => (
                <div
                  key={s.id}
                  className={`flex items-center gap-6 rounded-xl bg-card p-4 ring-1 ring-border ${i === 0 ? "" : "opacity-70"}`}
                >
                  <div className="flex flex-none flex-col items-center border-r border-border pr-6">
                    <span className="font-mono text-[10px] font-medium uppercase text-muted-foreground">Call</span>
                    <span className="font-mono text-lg font-medium">{s.callTime}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-medium">{s.title}</h3>
                    <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <MapPin className="size-3" />
                      {s.location}
                    </p>
                  </div>
                  <StatusBadge status={s.status} />
                </div>
              ))}
              {today.length === 0 && (
                <p className="rounded-xl bg-card p-6 text-center text-sm text-muted-foreground ring-1 ring-border">
                  No shoots scheduled today.
                </p>
              )}
            </div>
          </section>

          {/* Recent inquiries */}
          <section>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Recent Inquiries
              </h2>
              <Link to="/leads" className="text-xs font-medium text-brand hover:underline">
                View all
              </Link>
            </div>
            <div className="overflow-hidden rounded-xl bg-card ring-1 ring-border">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-border bg-surface-muted/50">
                  <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Client</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Budget</th>
                    <th className="px-4 py-3 text-right font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recentInquiries.map((i) => (
                    <tr key={i.id}>
                      <td className="px-4 py-3 font-medium">{i.client}</td>
                      <td className="px-4 py-3 text-muted-foreground">{i.category}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{i.date.toUpperCase()}</td>
                      <td className="px-4 py-3 font-mono text-muted-foreground">{fmtUsd(i.budget, { compact: true })}</td>
                      <td className="px-4 py-3 text-right"><StatusBadge status={i.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* Right rail */}
        <div className="space-y-8">
          <section>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Recent Galleries
              </h2>
              <Link to="/galleries" className="text-xs font-medium text-brand hover:underline">
                Library
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {recentGalleries.map((g) => (
                <Link key={g.id} to="/galleries" className="group space-y-2">
                  <div
                    className="aspect-[4/5] w-full rounded-xl ring-1 ring-inset ring-border transition group-hover:ring-foreground/20"
                    style={{
                      background: `linear-gradient(135deg, oklch(0.6 0.08 ${g.coverHue}) 0%, oklch(0.3 0.04 ${g.coverHue}) 100%)`,
                    }}
                  />
                  <div className="leading-tight">
                    <p className="truncate text-[11px] font-medium">{g.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      {g.imageCount} img · {g.status}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <section>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Upcoming Payments
              </h2>
              <Link to="/invoices" className="text-xs font-medium text-brand hover:underline">
                Invoices
              </Link>
            </div>
            <div className="divide-y divide-border rounded-xl bg-card ring-1 ring-border">
              {upcomingPayments.map((p) => (
                <div key={p.id} className="flex items-center justify-between p-4">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{p.label}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      {p.overdue ? <span className="text-danger">Overdue · {p.due}</span> : <>Due {p.due}</>}
                    </p>
                  </div>
                  <p className="font-mono text-sm font-medium">{fmtUsd(p.amount)}</p>
                </div>
              ))}
            </div>
          </section>

          <Link
            to="/reports"
            className="flex items-center justify-between rounded-xl border border-dashed border-border-strong bg-card/50 p-4 text-sm hover:bg-card"
          >
            <span className="font-medium">View monthly report</span>
            <ArrowUpRight className="size-4 text-muted-foreground" />
          </Link>
        </div>
      </div>
    </div>
  );
}
